import React from 'react';

/**
 * Stylized SVG map of Africa with priority markets highlighted by phase.
 * Lightweight inline SVG (no external data dependency). Uses approximate
 * country outline groupings rendered as dots/regions for visual punch.
 *
 * Props:
 *  - markets: array of { name, lon, lat, tier: 1|2|3 }
 *  - height: e.g. "h-[420px]"
 */

// Approximate lon/lat for plotting (capitalish). Plotted using a simple
// equirectangular projection scaled to fit the chosen viewBox.
const MARKETS = [
  // Tier 1 (anchor 2026-2030)
  { name: 'Kenya',        lon: 36.8, lat: -1.3, tier: 1 },
  { name: 'Tanzania',     lon: 35.7, lat: -6.4, tier: 1 },
  { name: 'Rwanda',       lon: 30.1, lat: -1.9, tier: 1 },
  { name: 'Uganda',       lon: 32.6, lat:  1.4, tier: 1 },
  { name: 'Zambia',       lon: 28.3, lat: -15.4,tier: 1 },
  { name: 'Botswana',     lon: 25.9, lat: -24.6,tier: 1 },
  { name: 'DRC',          lon: 15.3, lat: -4.3, tier: 1 },
  { name: 'Ghana',        lon: -0.2, lat:  5.6, tier: 1 },
  // Tier 2 (2029-2035)
  { name: 'Namibia',      lon: 17.1, lat: -22.6,tier: 2 },
  { name: 'South Africa', lon: 28.2, lat: -26.2,tier: 2 },
  { name: 'Angola',       lon: 13.2, lat: -8.8, tier: 2 },
  { name: "Côte d'Ivoire",lon: -4.0, lat:  5.4, tier: 2 },
  { name: 'Senegal',      lon: -17.4,lat: 14.7, tier: 2 },
  { name: 'Ethiopia',     lon: 38.7, lat:  9.0, tier: 2 },
  { name: 'Nigeria',      lon:  7.4, lat:  9.1, tier: 2 },
  { name: 'Mozambique',   lon: 32.6, lat: -25.9,tier: 2 },
  // Tier 3 (2033-2040)
  { name: 'Republic of the Congo', lon: 15.3, lat: -0.7, tier: 3 },
  { name: 'Gabon',        lon: 11.6, lat: -0.4, tier: 3 },
  { name: 'Benin',        lon:  2.4, lat:  6.5, tier: 3 },
  { name: 'Zimbabwe',     lon: 31.0, lat: -17.8,tier: 3 },
];

// viewBox bounds for the projection
const VB = { w: 600, h: 700 };
const LON_MIN = -20, LON_MAX = 52;
const LAT_MIN = -36, LAT_MAX = 38;

const project = (lon, lat) => {
  const x = ((lon - LON_MIN) / (LON_MAX - LON_MIN)) * VB.w;
  const y = ((LAT_MAX - lat) / (LAT_MAX - LAT_MIN)) * VB.h;
  return [x, y];
};

const TIER_STYLES = {
  1: { fill: '#7fd6c9', ring: '#2f9e8e', label: 'Tier 1 · Anchor' },
  2: { fill: '#c19a3c', ring: '#a8842f', label: 'Tier 2 · Regional' },
  3: { fill: '#94a3b8', ring: '#64748b', label: 'Tier 3 · Frontier' },
};

const AfricaMap = ({ className = '', showLabels = true, markets = MARKETS }) => {
  return (
    <div className={`relative ${className}`} data-testid="africa-map">
      <svg viewBox={`0 0 ${VB.w} ${VB.h}`} className="w-full h-auto">
        {/* Background hint of continent shape — stylised silhouette */}
        <defs>
          <radialGradient id="africa-glow" cx="50%" cy="40%" r="60%">
            <stop offset="0%" stopColor="#1a365d" stopOpacity="0.35" />
            <stop offset="60%" stopColor="#0a1729" stopOpacity="0.15" />
            <stop offset="100%" stopColor="#0a1729" stopOpacity="0" />
          </radialGradient>
          <filter id="dotGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="6" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Continent silhouette — simplified outline polygon */}
        <path
          d="
            M250 60
            L320 70
            L380 85
            L440 110
            L470 150
            L490 195
            L500 240
            L505 290
            L495 340
            L470 380
            L440 410
            L420 450
            L410 490
            L390 520
            L370 550
            L345 580
            L325 615
            L300 640
            L270 650
            L240 640
            L215 615
            L195 580
            L175 540
            L155 500
            L140 460
            L130 415
            L120 365
            L115 310
            L120 260
            L135 210
            L160 165
            L195 120
            L225 85
            Z
          "
          fill="url(#africa-glow)"
          stroke="rgba(127,214,201,0.18)"
          strokeWidth="1.5"
        />

        {/* Subtle grid */}
        <g stroke="rgba(127,214,201,0.06)" strokeWidth="0.5">
          {[0, 0.25, 0.5, 0.75, 1].map((p) => (
            <line key={`h${p}`} x1="0" y1={p * VB.h} x2={VB.w} y2={p * VB.h} />
          ))}
          {[0, 0.25, 0.5, 0.75, 1].map((p) => (
            <line key={`v${p}`} x1={p * VB.w} y1="0" x2={p * VB.w} y2={VB.h} />
          ))}
        </g>

        {/* Country markers */}
        {markets.map((m, idx) => {
          const [x, y] = project(m.lon, m.lat);
          const s = TIER_STYLES[m.tier];
          return (
            <g key={m.name}>
              {/* outer pulse for tier 1 */}
              {m.tier === 1 && (
                <circle cx={x} cy={y} r="14" fill={s.fill} opacity="0.18">
                  <animate attributeName="r" values="10;22;10" dur="3s" repeatCount="indefinite" begin={`${(idx % 5) * 0.3}s`} />
                  <animate attributeName="opacity" values="0.32;0;0.32" dur="3s" repeatCount="indefinite" begin={`${(idx % 5) * 0.3}s`} />
                </circle>
              )}
              <circle cx={x} cy={y} r={m.tier === 1 ? 6 : 4.5} fill={s.fill} stroke={s.ring} strokeWidth="1.5" filter="url(#dotGlow)" />
              {showLabels && (
                <text x={x + 10} y={y + 4} fill="#cbd5e1" fontSize="10" fontFamily="Helvetica, Arial, sans-serif">
                  {m.name}
                </text>
              )}
            </g>
          );
        })}
      </svg>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 mt-4 justify-center">
        {Object.entries(TIER_STYLES).map(([t, s]) => (
          <div key={t} className="inline-flex items-center gap-2 text-xs text-white/70">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: s.fill, boxShadow: `0 0 0 2px ${s.ring}` }} />
            {s.label}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AfricaMap;
